import { prettyErrorDescription } from 'constants/errors'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import toast from 'react-hot-toast'
import textServices from 'services/text-services'
import { Label } from 'theme/theme'
import Input from "components/input";
import { usePopupContext } from 'hooks/use-main-cass'
import ReCAPTCHA from 'react-google-recaptcha'
import { useContext, useEffect, useRef } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import ProfileService from 'services/profile-services'
import { useSession } from 'contexts/session-context'
import { dlTrackError } from 'components/dataLayer/dataLayer'
import { AxiosError } from 'axios'
import OtpPhoneV2 from './OtpPhone'

interface IProps {
  defaultValues: { phoneNumber?: string }
}

const EditPhoneV2 = ({ defaultValues }: IProps) => {
  const { closePopup, openPopup } = usePopupContext()
  const { setLoad } = useContext(LoaderContext)
  const [{ data: session }, { setUser }] = useSession()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["phoneNumber"])),
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })

  useEffect(() => {
    if (defaultValues) {
      setValue('phoneNumber', defaultValues?.phoneNumber)
    }
  }, [defaultValues])

  const handleError = (error: AxiosError) => {
    if (error?.isAxiosError) {
      const { code: errorCode, message: errorMessage } = error?.response?.data ?? {}
      dlTrackError({
        error_type: 'login',
        error_code: { errorCode },
        error_message: { errorMessage },
        section: 'login'
      })
      toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
    }
  }

  const onSubmit = async (code: string) => {
    setLoad(true)
    try {
      const response = await ProfileService.otpVerifyPhone({ code, session })
      if (response?.status === 200) {
        setUser(response.data)
        toast.success('Los cambios se guardaron con éxito', { className: 'toast__success' })
        closePopup()
      }
    } catch (err) {
      handleError(err)
    } finally {
      setLoad(false)
    }
  }

  async function onFormSubmit(data: any) {
    recaptchaRef.current?.reset()
    await recaptchaRef.current?.executeAsync().then(async (recaptchaToken: any) => {
      setLoad(true)
      try {
        const result = await ProfileService.otpGenerate({ phone: data.phoneNumber, recaptchaToken, session })
        if (result.status === 201) {
          openPopup(<OtpPhoneV2 getValues={getValues} onSubmit={onSubmit} />)
        } else if (result.status === 200) {
          closePopup()
        }
      } catch (err) {
        handleError(err)
      } finally {
        setLoad(false)
      }
    })
  }

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  return (
    <>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITEKEY} />
      <div className="pb-4">
        <h2 className="form__sign-in__title">Editar contacto</h2>
        <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className='space-y-4'>
          <div className={`gap-2 flex flex-col input-container ${errors?.phoneNumber?.message ? 'error-input' : ''}`}>
            <div className='flex items-center gap-2'>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.05762 0.525146C3.31152 0.525146 3.54102 0.608154 3.74609 0.77417L3.79004 0.803467L5.70898 2.78101L5.69434 2.79565C5.89941 2.99097 6.00195 3.23022 6.00195 3.51343C6.00195 3.79663 5.9043 4.03589 5.70898 4.2312L4.77148 5.1687C5.10352 5.93042 5.53809 6.56519 6.0752 7.073C6.63184 7.60034 7.27148 8.03979 7.99414 8.39136L8.93164 7.45386C9.12695 7.25854 9.37109 7.16089 9.66406 7.16089C9.95703 7.16089 10.2012 7.25854 10.3965 7.45386L10.4111 7.48315L12.3154 9.38745C12.5107 9.58276 12.6084 9.8269 12.6084 10.1199C12.6084 10.4031 12.5107 10.6423 12.3154 10.8376L10.8359 12.3171C10.6113 12.5125 10.3525 12.6394 10.0596 12.698C9.77637 12.7566 9.49316 12.7371 9.20996 12.6394H9.19531C7.0957 11.8191 5.32324 10.6863 3.87793 9.24097C2.99902 8.36206 2.23242 7.37085 1.57812 6.26733C1.10938 5.46655 0.743164 4.6853 0.479492 3.92358V3.90894C0.381836 3.6355 0.367188 3.35718 0.435547 3.07397C0.503906 2.78101 0.650391 2.53198 0.875 2.3269L0.860352 2.31226L2.33984 0.788818L2.36914 0.77417C2.57422 0.608154 2.80371 0.525146 3.05762 0.525146ZM3.05762 1.46265C3.02832 1.46265 2.99414 1.47729 2.95508 1.50659L1.50488 2.98608C1.42676 3.05444 1.37305 3.1521 1.34375 3.27905C1.31445 3.39624 1.31934 3.50366 1.3584 3.60132C1.60254 4.29468 1.94434 5.02222 2.38379 5.78394C2.99902 6.82886 3.7168 7.76147 4.53711 8.58179C5.86523 9.90991 7.52051 10.9646 9.50293 11.7458C9.77637 11.8337 10.0156 11.7898 10.2207 11.614L11.6855 10.1492C11.7051 10.1296 11.7148 10.1199 11.7148 10.1199C11.7148 10.1101 11.7051 10.0955 11.6855 10.0759L9.72266 8.11304C9.69336 8.08374 9.67383 8.06909 9.66406 8.06909C9.6543 8.06909 9.63477 8.08374 9.60547 8.11304L8.19922 9.51929L7.54004 9.21167C7.22754 9.05542 6.9248 8.87964 6.63184 8.68433C6.22168 8.42065 5.85547 8.14233 5.5332 7.84937L5.43066 7.76147C5.09863 7.43921 4.78613 7.06323 4.49316 6.63354C4.28809 6.32104 4.10254 5.9939 3.93652 5.6521L3.76074 5.24194L3.6582 4.94897L3.87793 4.7439L5.0498 3.57202C5.0791 3.54272 5.08887 3.50854 5.0791 3.46948L3.16016 1.50659C3.12109 1.47729 3.08691 1.46265 3.05762 1.46265Z" fill="white" />
              </svg>
              <Label className="text-start text-2xl font-bold uppercase input-label">
                {textServices.findByText('otp/phone/title', { es: 'Número de celular', en: 'Phone number' })}
              </Label>
            </div>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder="Ingresa tu número"
              name="phoneNumber"
              {...register("phoneNumber")}
            />
          </div>
          <p className='text medium text-center'>Necesitamos validar tu número de teléfono para completar el registro. Te enviaremos un código OTP para que ingreses en la siguiente pantalla.</p>
          <div className='space-y-2'>
            <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">
              Pedir código OTP
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default EditPhoneV2
